import React, { useState } from 'react'
import { GetServerSideProps } from 'next'
import { urls } from '../src/utils/config'
import { Amplitude, LogOnMount } from '@amplitude/react-amplitude'
import { NextSeo } from 'next-seo'
import { makeStyles, Grid, Typography, Button, Link } from '@material-ui/core'

import FiltersProvider from '../src/contexts/Filters'
import Nav from '../src/components/Nav'
import ProductNav from '../src/components/Product/Nav'
import ExchangeGuide from '../src/components/Product/exchanges/p2p/ExchangeGuide'
import FAQSection from '../src/components/Product/FAQSection'
import LearnMore from '../src/components/Product/LearnMore'
import QuickLinks from '../src/components/Footer/QuickLinks'
import Footer from '../src/components/Footer'
import TopTenExchanges from '../src/components/Product/exchanges/p2p/ExchangesTable'

// Types
import { LoadState } from '../types/general'
import { Exchange } from '../types/exchange'

// API
import { getExchanges } from '../src/services/exchanges'

export interface Props {
  initialLoadState: LoadState
  p2pExchanges: Exchange[]
}

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    background: 'white',
  },
  heroSection: {
    background: 'linear-gradient(180deg, rgba(209, 197, 224, 0.2) 0.13%, #FFFFFF 100%)',
    overflow: 'auto',
  },
  section: {
    padding: '30px 20px',
    margin: '4rem auto',
    [theme.breakpoints.up('md')]: {
      padding: '30px 80px',
    },
    [theme.breakpoints.up('xl')]: {
      padding: '30px 0',
      maxWidth: '1200px',
    },
    '&>div': {
      padding: '0 20px',
    },
    '& h1': {
      [theme.breakpoints.down('sm')]: {
        fontSize: '54px',
      },
    },
    '& h1, & h3': {
      letterSpacing: '-0.055em',
      margin: '1rem 0',
    },
    '& p': {
      fontSize: '16px',
      color: '#7D7787',
    },
  },
  heroContentSection: {},
  heroCTA: {
    textTransform: 'unset',
    margin: '1rem 1rem 1rem 0',
  },
  link: {
    color: 'inherit',
    fontSize: '.8rem',
    padding: '.4rem 0',
    borderBottom: '1px solid',
  },
  videoWrapper: {
    position: 'relative',
    height: '360px',
    overflow: 'hidden',
    borderRadius: '1rem',

    '& iframe': {
      position: 'absolute',
      width: '100%',
      height: '100%',
      top: 0,
      left: 0,
    },
  },
  purpleText: {
    color: theme.palette.primary.main,
  },
}))

const Exchanges = ({ initialLoadState, p2pExchanges }: Props): JSX.Element => {
  const classes = useStyles({})
  const [loadState] = useState<LoadState>(initialLoadState)

  return (
    <Amplitude
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eventProperties={(inheritedProps: { page: any }) => ({
        ...inheritedProps,
        page: {
          ...inheritedProps.page,
          name: urls.productExchanges,
        },
      })}
    >
      {(): JSX.Element => (
        <FiltersProvider>
          <LogOnMount eventType="page-displayed" />
          <NextSeo
            title={`Cryptotesters.com - Cryptocurrency Exchanges`}
            description={`Cryptotesters.com | Crypto Exchanges allow you to buy at the best market rate cryptocurrency using conventional payment methods like by bank transfer or credit card.`}
            openGraph={{
              url: `https://cryptotesters.com/product/crypto-exchanges`,
              title: `Cryptotesters.com - Cryptocurrency Exchanges`,
              description: `Cryptotesters.com | Crypto Exchanges allow you to buy at the best market rate cryptocurrency using conventional payment methods like by bank transfer or credit card.`,
              images: [
                {
                  url: 'https://cryptotesters.com/sharing.png',
                  width: 145,
                  height: 56,
                  alt: `Cryptotesters.com`,
                },
              ],
              site_name: 'Cryptotesters.com',
            }}
            twitter={{
              handle: '@cryptotesters',
              site: '@cryptotesters',
              cardType: 'summary_large_image',
            }}
          />
          <div className={classes.pageWrapper}>
            <div className={classes.heroSection}>
              <Nav dark />
              <ProductNav />

              <Grid container className={classes.section}>
                <Grid item md={5} xs={12}>
                  <Typography variant="h1">
                    The best <span className={classes.purpleText}>peer to peer</span> exchanges
                  </Typography>
                  <Typography variant="body2">
                    Peer-to-peer exchanges allow you to buy cryptocurrencies from private sellers.
                    They are similar to marketplaces like eBay in the traditional world where buyers
                    & sellers can individually decide whatever price and payment method they want to
                    set.
                  </Typography>

                  <Button
                    color="primary"
                    variant="contained"
                    href="#exchanges"
                    className={classes.heroCTA}
                  >
                    Compare Exchanges
                  </Button>
                  <Link className={classes.link} underline="none">
                    See the whole guide
                  </Link>
                </Grid>

                <Grid item md={7} xs={12}>
                  <div>
                    <div className={classes.videoWrapper}>
                      <iframe
                        width="560"
                        height="315"
                        src="https://www.youtube.com/embed/GE1-FK1-YFw"
                        title="YouTube video player"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  </div>
                </Grid>
              </Grid>
            </div>

            <div id="exchanges">
              <TopTenExchanges exchanges={p2pExchanges} />
              <ExchangeGuide />
              <FAQSection />
              <LearnMore />
            </div>
            <QuickLinks />
            <Footer />
          </div>
        </FiltersProvider>
      )}
    </Amplitude>
  )
}

export default Exchanges

export const getServerSideProps: GetServerSideProps = async () => {
  let loadState = LoadState.IDLE
  let p2pExchanges: Exchange[] = []

  try {
    p2pExchanges = await getExchanges(['p2p'])
    loadState = LoadState.LOADED
  } catch (e) {
    loadState = LoadState.ERROR
  }

  return {
    props: {
      p2pExchanges,
      initialLoadState: loadState,
    },
  }
}
